<template>
  <div>
    <p v-if="loading"><Loading /></p>

    <div class="page">
      <!-- Mission Name Display -->
      <div v-if="mission.title" class="mission-name-header">
        {{ mission.title }}
      </div>

      <!-- Branch Code Input Form -->
      <div v-if="!token" class="form">
        <div class="field">
          <label class="field__label field--required">分店代碼</label>
          <input
            class="field__input"
            v-model="branchCode"
            placeholder="請輸入分店代碼"
            :disabled="loading"
          />
          <small class="field__desc">請輸入您的分店代碼以產生專屬 QR Code</small>
        </div>

        <SharedButton
          class="s-btn s-btn-bg-primary s-mt-3"
          :disabled="loading || !branchCode"
          @click="periodicallyGenerateQrCode"
        >
          {{ loading ? '產生中...' : '產生 QR Code' }}
        </SharedButton>
      </div>

      <!-- QR Code Display -->
      <div v-else>
        <b-card class="text-center p-4 mb-4" no-body>
          <div class="mb-3">
            <vue-qr :text="qrUrl" :size="300" class="mx-auto"></vue-qr>
          </div>

          <!-- Countdown Timer -->
          <div class="mb-3">
            <small class="text-muted">QR Code 將在 {{ countdown }} 秒後自動更新</small>
          </div>

          <!-- Update QRCode Button -->
          <SharedButton
            class="s-btn s-btn-bg-primary"
            :disabled="loading"
            @click="refreshQrCode"
          >
            {{ loading ? '更新中...' : '立即更新 QRCode' }}
          </SharedButton>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import VueQr from 'vue-qr'
import { mapGetters } from "vuex";
import Loading from '@/components/Page/Liff/Shared/Loading'
import rewardMissionApi from '@/apis/liff/v2/reward-mission'
import SharedButton from "@/components/Page/Liff/Shared/Button";

export default {
  name: 'RewardMissionDynamicVerification',
  components: {
    VueQr,
    Loading,
    SharedButton
  },
  data() {
    return {
      branchCode: '',
      token: null,
      qrUrl: '',
      countdown: null,
      loading: false,
      intervalId: null,
      countdownIntervalId: null,
      refreshInterval: 30,
      mission: {
        title: '',
      }
    }
  },
  computed: {
    ...mapGetters({
      liffId: "liffGeneral/liffId",
    }),
    missionId() {
      return this.$route.params.mission_id
    },
  },
  created() {
    this.fetchMissionDetails()
  },
  methods: {
    async fetchMissionDetails() {
      try {
        this.mission = await rewardMissionApi.getRewardMission(this.missionId)
      } catch (error) {
        console.error('Failed to fetch mission details:', error)
      }
    },

    async periodicallyGenerateQrCode() {
      await this.generateQrCode()
      this.startCountdownAndRefreshToken()
      this.countdown = this.refreshInterval
    },

    async refreshQrCode() {
      // Clear existing countdown interval
      if (this.countdownIntervalId) {
        clearInterval(this.countdownIntervalId)
      }

      // Generate new QR code and restart countdown
      await this.periodicallyGenerateQrCode()
    },

    async generateQrCode() {
      this.loading = true

      try {
        const response = await rewardMissionApi.generateVerificationToken(this.missionId, {
          branch_code: this.branchCode
        })

        this.token = response.token
        this.qrUrl = `https://liff.line.me/${this.liffId}/rewards-card/mission/${this.missionId}?token=${this.token}`
        console.log( `https://liff.line.me/${this.liffId}/rewards-card/mission/${this.missionId}?token=${this.token}`)
      } catch (error) {
        console.log(error)
        this.$swal.fire({
          title: error.response?.data?.message || '產生 QR Code 失敗，請重試',
          type: "error",
        });
      } finally {
        this.loading = false
      }
    },

    startCountdownAndRefreshToken() {
      this.countdownIntervalId = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
        }

        if (this.countdown === 0) {
          clearInterval(this.countdownIntervalId)

          this.generateQrCode()
          this.countdown = this.refreshInterval
          this.startCountdownAndRefreshToken()
        }
      }, 1000)
    },
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId)
    }
  }
}
</script>

<style lang="scss" scoped>
.mission-name-header {
  padding: 16px 20px;
  background: #ffffff;
  color: #ff0000;
  font-weight: 600;
  font-size: 18px;
  border-bottom: 1px solid #e5e5ea;
  text-align: center;
}

.form {
  padding: 20px 10px;
  background: #ffffff;
}

.field {
  &:not(:last-of-type) {
    margin-bottom: 24px;
  }

  &__label {
    font-weight: 400;
    font-size: 14px;
    color: var(--liff-primary_text_color);
    margin-bottom: 8px;
    display: block;
  }

  &__desc {
    color: var(--liff-secondary_text_color);
    font-size: 12px;
    line-height: 1;
    margin-top: 4px;
  }

  &__input {
    font-weight: 500;
    font-size: 16px;
    color: var(--liff-primary_text_color);
    border: 1px solid #e5e5ea;
    border-radius: 5px;
    background: transparent;
    padding: 8px 12px;
    width: 100%;

    &::placeholder {
      color: #b7b7b7;
    }

    &:focus {
      border: 1px solid var(--liff-primary_text_color);
      outline: none;
    }

    &:disabled {
      background-color: #f0f0f0;
    }
  }

  &--required {
    &:after {
      content: "*";
      position: relative;
      font-family: "PingFang TC";
      color: var(--s-primary);
      margin-left: 2px;
    }
  }
}
</style>
